import type {
  ReportColumn,
  ReportContent,
  ReportKpi,
} from "@energyplatform/client";
import type { XlsxCell, XlsxSheet } from "./xlsx.ts";

// Pure logic of report pages and exports, shared with the tests. Labels come from the caller (i18n), numbers are
// formatted by the caller's locale; a gap is always a dash, never a zero.

export interface Formatters {
  number: (value: number | null, digits?: number) => string;
  share: (value: number | null) => string;
  time: (iso: string | null) => string;
  /** A code of a column kind (eventKind, priority, stage, connectionState, code) as text. */
  code: (kind: string, value: string | number) => string;
  label: (key: string) => string;
}

/** Units as people write them: degC as °C, a dimensionless "1" as nothing. */
export function unitText(unit: string | null | undefined): string {
  switch (unit) {
    case "degC":
      return "°C";
    case "percent":
      return "%";
    case "1":
    case null:
    case undefined:
      return "";
    case "PLN":
      return "zł";
    default:
      return unit;
  }
}

/** Large energies read better in MWh. */
export function energyValue(kwh: number): {
  value: number;
  unit: "kWh" | "MWh";
} {
  return Math.abs(kwh) >= 10_000
    ? { value: kwh / 1000, unit: "MWh" }
    : { value: kwh, unit: "kWh" };
}

/** A headline figure with its unit: "16,1 MWh", "312 kW", "41 %", "4", "38 min". */
export function kpiText(
  k: Pick<ReportKpi, "value" | "unit">,
  f: Formatters,
): string {
  if (k.value === null) {
    return f.number(null);
  }
  switch (k.unit) {
    case "kWh": {
      const e = energyValue(k.value);
      return `${f.number(e.value, e.unit === "MWh" ? 1 : 0)} ${e.unit}`;
    }
    case "kW":
      return `${f.number(k.value, 1)} kW`;
    case "share":
      return f.share(k.value);
    case "count":
      return f.number(k.value, 0);
    case "min":
      return `${f.number(k.value, 0)} min`;
    case "PLN":
      return `${f.number(k.value, 0)} zł`;
    case "PLN/MWh":
      return `${f.number(k.value, 0)} zł/MWh`;
    case "PLN/kWh":
      return `${f.number(k.value, 3)} zł/kWh`;
    case "tanPhi":
      return f.number(k.value, 2);
    case "kvarh":
      return `${f.number(k.value, 0)} kvarh`;
    default:
      return `${f.number(k.value)} ${unitText(k.unit)}`.trim();
  }
}

/**
 * How a figure moved against the comparison period: relative for amounts ("+4 %"), in percentage points for shares
 * ("+19 pp"); null when either side is missing or the base is zero.
 */
export function kpiChange(
  k: ReportKpi,
): { kind: "relative" | "points"; value: number } | null {
  if (k.value === null || k.previous === null) {
    return null;
  }
  if (k.unit === "share") {
    return { kind: "points", value: (k.value - k.previous) * 100 };
  }
  return k.previous === 0
    ? null
    : {
        kind: "relative",
        value: (k.value - k.previous) / Math.abs(k.previous),
      };
}

/** One table cell as text, by the column's kind. */
export function cellText(
  column: ReportColumn,
  value: string | number | null,
  f: Formatters,
): string {
  if (value === null || value === "") {
    return f.number(null);
  }
  switch (column.kind) {
    case "number":
      // tan φ is read to two decimals against its threshold (0.4).
      if (column.key === "tanPhi" && typeof value === "number") {
        return f.number(value, 2);
      }
      return typeof value === "number"
        ? `${f.number(value, Math.abs(value) >= 100 ? 0 : 1)}${column.unit && unitText(column.unit) ? ` ${unitText(column.unit)}` : ""}`
        : String(value);
    case "percent":
      return typeof value === "number" ? f.share(value) : String(value);
    case "time":
      return f.time(String(value));
    case "text":
      return String(value);
    default:
      return f.code(column.kind, value);
  }
}

const SEPARATOR = ";";

function csvField(v: string): string {
  return /[;"\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * The content as CSV for spreadsheets: a heading with the report, period, data version and time zone, then each
 * section (figures, tables, series) with units in the headers. Separator ";" and a BOM, so Excel in Polish or German
 * locales opens it correctly.
 */
export function toReportCsv(
  meta: { name: string; period: string; version: string; generated: string },
  content: ReportContent,
  f: Formatters,
): string {
  const rows: string[][] = [
    [f.label("csv.report"), meta.name],
    [f.label("csv.place"), content.nodeName],
    [f.label("csv.period"), meta.period],
    [f.label("csv.generated"), meta.generated],
    [f.label("csv.version"), meta.version],
    [f.label("csv.timeZone"), content.timeZone],
    [f.label("csv.completeness"), f.share(content.completeness)],
  ];
  // Numbers without grouping, in the locale's decimal notation, so a spreadsheet reads them as numbers.
  const num = (v: number | null) =>
    v === null ? "" : f.number(v, 3).replace(/\s| | /g, "");
  for (const s of content.sections) {
    rows.push([], [s.title ?? f.label(`heading.${content.template}.${s.key}`)]);
    if (s.kpis) {
      rows.push([
        f.label("csv.figure"),
        f.label("csv.value"),
        f.label("csv.unit"),
        f.label("csv.previous"),
      ]);
      for (const k of s.kpis) {
        rows.push([
          f.label(`kpi.${k.key}`),
          num(k.value),
          k.unit,
          num(k.previous),
        ]);
      }
    }
    if (s.table) {
      rows.push(
        s.table.columns.map(
          (c) => f.label(`col.${c.key}`) + (c.unit ? ` [${c.unit}]` : ""),
        ),
      );
      for (const r of s.table.rows) {
        rows.push(
          s.table.columns.map((c, i) => {
            const v = r[i] ?? null;
            return c.kind === "number" || c.kind === "percent"
              ? typeof v === "number"
                ? num(v)
                : ""
              : v === null
                ? ""
                : cellText(c, v, f);
          }),
        );
      }
    }
    if (s.chart) {
      rows.push([
        f.label("csv.label"),
        ...s.chart.series.map(
          (x) =>
            `${f.label(`series.${x.key}`)} [${x.axis === "right" && s.chart!.unit2 ? s.chart!.unit2 : s.chart!.unit}]`,
        ),
      ]);
      s.chart.labels.forEach((l, i) =>
        rows.push([l, ...s.chart!.series.map((x) => num(x.values[i] ?? null))]),
      );
    }
  }
  return (
    "﻿" + rows.map((r) => r.map(csvField).join(SEPARATOR)).join("\r\n") + "\r\n"
  );
}

/**
 * The content as an XLSX workbook: a "report" sheet with the report, period, data version, time zone, completeness
 * and notes, then one sheet per section with units in the headers. Numbers stay numbers, so the spreadsheet can
 * sum and chart them in any locale.
 */
export function toReportSheets(
  meta: { name: string; period: string; version: string; generated: string },
  content: ReportContent,
  f: Formatters,
): XlsxSheet[] {
  const head = (s: string) => ({ bold: s });
  const summary: XlsxCell[][] = [
    [head(f.label("csv.report")), meta.name],
    [head(f.label("csv.place")), content.nodeName],
    [head(f.label("csv.period")), meta.period],
    [head(f.label("csv.generated")), meta.generated],
    [head(f.label("csv.version")), meta.version],
    [head(f.label("csv.timeZone")), content.timeZone],
    [head(f.label("csv.completeness")), content.completeness],
    ...content.notes.map((n): XlsxCell[] => [
      head(f.label("csv.note")),
      f.label(`note.${n}`),
    ]),
  ];
  const sheets: XlsxSheet[] = [
    { name: f.label("csv.report"), rows: summary, widths: [22, 60] },
  ];
  for (const s of content.sections) {
    const rows: XlsxCell[][] = [];
    if (s.kpis) {
      rows.push(
        [
          f.label("csv.figure"),
          f.label("csv.value"),
          f.label("csv.unit"),
          f.label("csv.previous"),
        ].map(head),
      );
      for (const k of s.kpis) {
        rows.push([f.label(`kpi.${k.key}`), k.value, k.unit, k.previous]);
      }
    }
    if (s.table) {
      rows.push(
        s.table.columns.map((c) =>
          head(f.label(`col.${c.key}`) + (c.unit ? ` [${c.unit}]` : "")),
        ),
      );
      for (const r of s.table.rows) {
        rows.push(
          s.table.columns.map((c, i) => {
            const v = r[i] ?? null;
            if (c.kind === "number" || c.kind === "percent") {
              return typeof v === "number" ? v : null;
            }
            return v === null ? null : cellText(c, v, f);
          }),
        );
      }
    }
    if (s.chart) {
      if (rows.length > 0) {
        rows.push([]);
      }
      rows.push([
        head(f.label("csv.label")),
        ...s.chart.series.map((x) =>
          head(`${f.label(`series.${x.key}`)} [${s.chart!.unit}]`),
        ),
      ]);
      s.chart.labels.forEach((l, i) =>
        rows.push([l, ...s.chart!.series.map((x) => x.values[i] ?? null)]),
      );
    }
    sheets.push({
      name: s.title ?? f.label(`heading.${content.template}.${s.key}`),
      rows,
      widths: [
        30,
        ...Array<number>(Math.max(0, (rows[0]?.length ?? 1) - 1)).fill(16),
      ],
    });
  }
  return sheets;
}
